import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Plus, Trash2 } from "lucide-react";
import { PageHeader } from "@/components/ui/page-header";
import { ResumeDocmosisService, type ResumeData } from "@/services/resumeDocmosisService";
import { toast } from "sonner";

export default function ResumeDocmosisBuilder() {
  const [isGenerating, setIsGenerating] = useState(false);
  const [resumeData, setResumeData] = useState<ResumeData>({
    Name: "",
    Phone: "",
    Email: "",
    City: "",
    State: "",
    LinkedIn: "",
    Summary: "",
    Skills: [],
    CareerHighlights: [],
    ProfessionalExperience: [],
    CertificationsAndLicenses: [],
    TechnicalSkills: [],
    Languages: [],
    AwardsAndHonors: [],
    ProfessionalAffiliations: [],
    Publications: [],
    Projects: [],
    Presentations: [],
    Portfolio: [],
    TrainingAndDevelopment: [],
    MilitaryExperience: [],
    VolunteerMemberships: [],
    Education: []
  });

  const updateBasicInfo = (field: keyof Pick<ResumeData, 'Name' | 'Phone' | 'Email' | 'City' | 'State' | 'LinkedIn' | 'Summary'>, value: string) => {
    setResumeData(prev => ({ ...prev, [field]: value }));
  };

  const addSkill = () => {
    setResumeData(prev => ({
      ...prev,
      Skills: [...prev.Skills, { Skill: "" }]
    }));
  };

  const updateSkill = (index: number, value: string) => {
    setResumeData(prev => ({
      ...prev,
      Skills: prev.Skills.map((skill, i) => i === index ? { Skill: value } : skill)
    }));
  };

  const removeSkill = (index: number) => {
    setResumeData(prev => ({
      ...prev,
      Skills: prev.Skills.filter((_, i) => i !== index)
    }));
  };

  const addCareerHighlight = () => {
    setResumeData(prev => ({
      ...prev,
      CareerHighlights: [...prev.CareerHighlights, { chtitle: "", chdescription: "" }]
    }));
  };

  const updateCareerHighlight = (index: number, field: 'chtitle' | 'chdescription', value: string) => {
    setResumeData(prev => ({
      ...prev,
      CareerHighlights: prev.CareerHighlights.map((highlight, i) => 
        i === index ? { ...highlight, [field]: value } : highlight
      )
    }));
  };

  const removeCareerHighlight = (index: number) => {
    setResumeData(prev => ({
      ...prev,
      CareerHighlights: prev.CareerHighlights.filter((_, i) => i !== index)
    }));
  };

  const addExperience = () => {
    setResumeData(prev => ({
      ...prev,
      ProfessionalExperience: [...prev.ProfessionalExperience, {
        job_title: "",
        company: "",
        location: "",
        start_date: "",
        end_date: "",
        Descriptions: [{ Description: "" }]
      }]
    }));
  };

  const updateExperience = (index: number, field: keyof Omit<ResumeData['ProfessionalExperience'][0], 'Descriptions'>, value: string) => {
    setResumeData(prev => ({
      ...prev,
      ProfessionalExperience: prev.ProfessionalExperience.map((exp, i) => 
        i === index ? { ...exp, [field]: value } : exp
      )
    }));
  };

  const addExperienceDescription = (expIndex: number) => {
    setResumeData(prev => ({
      ...prev,
      ProfessionalExperience: prev.ProfessionalExperience.map((exp, i) => 
        i === expIndex ? { ...exp, Descriptions: [...exp.Descriptions, { Description: "" }] } : exp
      )
    }));
  };

  const updateExperienceDescription = (expIndex: number, descIndex: number, value: string) => {
    setResumeData(prev => ({
      ...prev,
      ProfessionalExperience: prev.ProfessionalExperience.map((exp, i) => 
        i === expIndex ? {
          ...exp,
          Descriptions: exp.Descriptions.map((desc, j) => 
            j === descIndex ? { Description: value } : desc
          )
        } : exp
      )
    }));
  };

  const removeExperience = (index: number) => {
    setResumeData(prev => ({
      ...prev,
      ProfessionalExperience: prev.ProfessionalExperience.filter((_, i) => i !== index)
    }));
  };

  const addEducation = () => {
    setResumeData(prev => ({
      ...prev,
      Education: [...prev.Education, {
        College_Degree_Name: "",
        Major: "",
        College_Name: "",
        City: "",
        ST: "",
        Year_Earned: ""
      }]
    }));
  };

  const updateEducation = (index: number, field: keyof ResumeData['Education'][0], value: string) => {
    setResumeData(prev => ({
      ...prev,
      Education: prev.Education.map((edu, i) => 
        i === index ? { ...edu, [field]: value } : edu
      )
    }));
  };

  const removeEducation = (index: number) => {
    setResumeData(prev => ({
      ...prev,
      Education: prev.Education.filter((_, i) => i !== index)
    }));
  };

  const generateResume = async () => {
    try {
      setIsGenerating(true);

      // Basic validation
      if (!resumeData.Name.trim()) {
        toast.error("Please enter your name");
        return;
      }

      if (!resumeData.Email.trim()) {
        toast.error("Please enter your email");
        return;
      }

      const blob = await ResumeDocmosisService.generateResume(resumeData);
      
      // Create proper filename with name, job title, company and date
      const currentDate = new Date().toLocaleString('en-US', { 
        month: 'short', 
        year: 'numeric' 
      });
      const jobTitle = resumeData.ProfessionalExperience.length > 0 && resumeData.ProfessionalExperience[0].job_title 
        ? ` - ${resumeData.ProfessionalExperience[0].job_title}` : '';
      const company = resumeData.ProfessionalExperience.length > 0 && resumeData.ProfessionalExperience[0].company 
        ? ` - ${resumeData.ProfessionalExperience[0].company}` : '';
      const filename = `${resumeData.Name}${jobTitle}${company} - ${currentDate}.docx`;
      
      ResumeDocmosisService.downloadBlob(blob, filename);
      toast.success("Resume generated successfully!");

    } catch (error) {
      console.error('Resume generation error:', error);
      toast.error(error instanceof Error ? error.message : "Failed to generate resume");
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <PageHeader 
        title="Resume Builder (Docmosis)" 
        description="Create professional resumes with our advanced builder" 
        backTo="/"
      />
      
      <div className="container mx-auto px-4 py-6 max-w-4xl">
        <Tabs defaultValue="basic" className="w-full">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="basic">Basic Info</TabsTrigger>
            <TabsTrigger value="skills">Skills</TabsTrigger>
            <TabsTrigger value="experience">Experience</TabsTrigger>
            <TabsTrigger value="education">Education</TabsTrigger>
            <TabsTrigger value="generate">Generate</TabsTrigger>
          </TabsList>

          <TabsContent value="basic" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Personal Information</CardTitle>
                <CardDescription>Enter your basic contact details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Full Name</Label>
                    <Input
                      id="name"
                      value={resumeData.Name}
                      onChange={(e) => updateBasicInfo('Name', e.target.value)}
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      value={resumeData.Phone}
                      onChange={(e) => updateBasicInfo('Phone', e.target.value)}
                      placeholder="Your phone number"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={resumeData.Email}
                      onChange={(e) => updateBasicInfo('Email', e.target.value)}
                      placeholder="Your email address"
                    />
                  </div>
                  <div>
                    <Label htmlFor="linkedin">LinkedIn</Label>
                    <Input
                      id="linkedin"
                      value={resumeData.LinkedIn}
                      onChange={(e) => updateBasicInfo('LinkedIn', e.target.value)}
                      placeholder="Your LinkedIn profile"
                    />
                  </div>
                  <div>
                    <Label htmlFor="city">City</Label>
                    <Input
                      id="city"
                      value={resumeData.City}
                      onChange={(e) => updateBasicInfo('City', e.target.value)}
                      placeholder="Your city"
                    />
                  </div>
                  <div>
                    <Label htmlFor="state">State</Label>
                    <Input
                      id="state"
                      value={resumeData.State}
                      onChange={(e) => updateBasicInfo('State', e.target.value)}
                      placeholder="Your state"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="summary">Professional Summary</Label>
                  <Textarea
                    id="summary"
                    value={resumeData.Summary}
                    onChange={(e) => updateBasicInfo('Summary', e.target.value)}
                    placeholder="Write a brief professional summary..."
                    rows={4}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="skills" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Skills & Career Highlights</CardTitle>
                <CardDescription>Add your key skills and career achievements</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <Label className="text-base font-semibold">Skills</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addSkill}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Skill
                    </Button>
                  </div>
                  {resumeData.Skills.map((skill, index) => (
                    <div key={index} className="flex gap-2 mb-2">
                      <Input
                        value={skill.Skill}
                        onChange={(e) => updateSkill(index, e.target.value)}
                        placeholder="Enter a skill"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => removeSkill(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>

                <Separator />

                <div>
                  <div className="flex justify-between items-center mb-4">
                    <Label className="text-base font-semibold">Career Highlights</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addCareerHighlight}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Highlight
                    </Button>
                  </div>
                  {resumeData.CareerHighlights.map((highlight, index) => (
                    <div key={index} className="space-y-2 mb-4 p-4 border rounded-lg">
                      <div className="flex justify-between items-center">
                        <Label>Highlight {index + 1}</Label>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => removeCareerHighlight(index)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      <Input
                        value={highlight.chtitle}
                        onChange={(e) => updateCareerHighlight(index, 'chtitle', e.target.value)}
                        placeholder="Highlight title"
                      />
                      <Textarea
                        value={highlight.chdescription}
                        onChange={(e) => updateCareerHighlight(index, 'chdescription', e.target.value)}
                        placeholder="Highlight description"
                        rows={3}
                      />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="experience" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Professional Experience</CardTitle>
                <CardDescription>Add your work experience</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex justify-between items-center mb-4">
                  <Label className="text-base font-semibold">Work Experience</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addExperience}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Position
                  </Button>
                </div>
                {resumeData.ProfessionalExperience.map((exp, expIndex) => (
                  <div key={expIndex} className="space-y-4 mb-6 p-4 border rounded-lg">
                    <div className="flex justify-between items-center">
                      <Label>Position {expIndex + 1}</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => removeExperience(expIndex)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Input
                        value={exp.job_title}
                        onChange={(e) => updateExperience(expIndex, 'job_title', e.target.value)}
                        placeholder="Job title"
                      />
                      <Input
                        value={exp.company}
                        onChange={(e) => updateExperience(expIndex, 'company', e.target.value)}
                        placeholder="Company name"
                      />
                      <Input
                        value={exp.location}
                        onChange={(e) => updateExperience(expIndex, 'location', e.target.value)}
                        placeholder="Location"
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          value={exp.start_date}
                          onChange={(e) => updateExperience(expIndex, 'start_date', e.target.value)}
                          placeholder="Start date"
                        />
                        <Input
                          value={exp.end_date}
                          onChange={(e) => updateExperience(expIndex, 'end_date', e.target.value)}
                          placeholder="End date"
                        />
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <Label>Job Descriptions</Label>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => addExperienceDescription(expIndex)}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add Description
                        </Button>
                      </div>
                      {exp.Descriptions.map((desc, descIndex) => (
                        <Textarea
                          key={descIndex}
                          value={desc.Description}
                          onChange={(e) => updateExperienceDescription(expIndex, descIndex, e.target.value)}
                          placeholder="Job responsibility or achievement"
                          className="mb-2"
                          rows={2}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="education" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Education</CardTitle>
                <CardDescription>Add your educational background</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex justify-between items-center mb-4">
                  <Label className="text-base font-semibold">Education</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addEducation}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Education
                  </Button>
                </div>
                {resumeData.Education.map((edu, index) => (
                  <div key={index} className="space-y-4 mb-6 p-4 border rounded-lg">
                    <div className="flex justify-between items-center">
                      <Label>Education {index + 1}</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => removeEducation(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Input
                        value={edu.College_Degree_Name}
                        onChange={(e) => updateEducation(index, 'College_Degree_Name', e.target.value)}
                        placeholder="Degree name"
                      />
                      <Input
                        value={edu.Major}
                        onChange={(e) => updateEducation(index, 'Major', e.target.value)}
                        placeholder="Major/Field of study"
                      />
                      <Input
                        value={edu.College_Name}
                        onChange={(e) => updateEducation(index, 'College_Name', e.target.value)}
                        placeholder="School/University name"
                      />
                      <Input
                        value={edu.City}
                        onChange={(e) => updateEducation(index, 'City', e.target.value)}
                        placeholder="City"
                      />
                      <Input
                        value={edu.ST}
                        onChange={(e) => updateEducation(index, 'ST', e.target.value)}
                        placeholder="State"
                      />
                      <Input
                        value={edu.Year_Earned}
                        onChange={(e) => updateEducation(index, 'Year_Earned', e.target.value)}
                        placeholder="Year earned"
                      />
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="generate" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Generate Resume</CardTitle>
                <CardDescription>Review your information and generate your professional resume</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-muted rounded-lg">
                  <h3 className="font-semibold mb-2">Resume Preview</h3>
                  <p><strong>Name:</strong> {resumeData.Name || 'Not provided'}</p>
                  <p><strong>Email:</strong> {resumeData.Email || 'Not provided'}</p>
                  <p><strong>Skills:</strong> {resumeData.Skills.filter(s => s.Skill.trim()).length} skills added</p>
                  <p><strong>Experience:</strong> {resumeData.ProfessionalExperience.filter(e => e.job_title.trim()).length} positions added</p>
                  <p><strong>Education:</strong> {resumeData.Education.filter(e => e.College_Degree_Name.trim()).length} degrees added</p>
                </div>
                
                <Button 
                  onClick={generateResume} 
                  disabled={isGenerating}
                  className="w-full"
                  size="lg"
                >
                  {isGenerating ? "Generating Resume..." : "Generate Resume"}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}